import React, { useCallback } from 'react';
import { defineMessages, useIntl, type MessageValue, type NoMessageValues } from 'react-intl';
import { Alert, Linking, StyleSheet, View } from 'react-native';

import type { AddressType } from '@sg/garnish';
import { Button, HighlightWords, IllusDelivery_1, Image } from '@sg/garnish';
import type { CourierDetails } from '@sg/graphql-schema';
import { FlattenedOrderStatuses } from '@sg/graphql-schema';

import { useTelemetry } from '@order/Telemetry';

export const OrderStatusCourierInfo = (props: OrderStatusCourierInfoProps) => {
  const { orderType, orderStatus, courierName, courierPhone } = props;

  const { formatMessage } = useIntl();
  const { track } = useTelemetry();

  const handlePress = useCallback(async () => {
    track('order_status.contact-courier', { courierPhone: courierPhone ?? '' });

    const url = `tel:${courierPhone}`;
    const isSupported = await Linking.canOpenURL(url);

    if (!isSupported) {
      track('order_status.contact-courier-unsupported');
      Alert.alert(formatMessage(messages.error));

      return;
    }

    await Linking.openURL(url);
  }, [courierPhone, formatMessage, track]);

  if (orderType !== 'delivery') return null;

  if (orderStatus !== FlattenedOrderStatuses.Delivering) return null;

  if (!courierName || !courierPhone) return null;

  return (
    <View style={styles.row}>
      <Image source={IllusDelivery_1} style={styles.illustration} />
      <View>
        <View style={styles.courier}>
          <HighlightWords
            size={4}
            text={formatMessage(messages.courier, {
              courier_name: courierName,
            })}
            highlights={[courierName]}
          />
        </View>
        <Button
          accessibilityLabel={formatMessage(messages.courierCta)}
          accessibilityHint={formatMessage(messages.courierCta)}
          accessibilityRole="button"
          onPress={handlePress}
        >
          {formatMessage(messages.courierCta)}
        </Button>
      </View>
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  courier: {
    flexDirection: 'row',
    width: 180,
    marginBottom: 8,
  },
  illustration: {
    width: 144,
    height: 144,
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────────

type OrderStatusCourierInfoProps = {
  orderType?: AddressType;
  orderStatus?: FlattenedOrderStatuses;
  courierName?: CourierDetails['name'];
  courierPhone?: CourierDetails['phone'];
};

// ─── Messages ────────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly error: NoMessageValues;
  readonly courier: { readonly courier_name: MessageValue };
  readonly courierCta: NoMessageValues;
}>({
  error: {
    defaultMessage: 'Something went wrong, try again in a few moments...',
    description: 'Order Status | Courier Info | Error message',
  },
  courier: {
    defaultMessage: '{courier_name} is on the way with your order.',
    description: 'Order Status | Courier Info | Courier is on the way with your order',
  },
  courierCta: {
    defaultMessage: 'Contact courier',
    description: 'Order Status | Courier Info | Contact courier button',
  },
});
